import type { ArcadePreset } from './types.js';

export interface ArcadePresetDefinition {
  id: ArcadePreset;
  name: string;
  detail: string;
  description: string;
}

export const ARCADE_PRESETS: readonly ArcadePresetDefinition[] = [
  {
    id: 'standard',
    name: 'STREET RULES',
    detail: 'CAREER ELIGIBLE',
    description: 'The authored NIGHTSHIFT handling, traffic and reward rules.',
  },
  {
    id: 'nitro-rush',
    name: 'NITRO RUSH',
    detail: 'BOOST NEVER SLEEPS',
    description: 'Nitro refills rapidly so every straight becomes an attack window.',
  },
  {
    id: 'heavy-metal',
    name: 'HEAVY METAL',
    detail: 'BIGGER SHUNTS',
    description: 'Stronger collision impulses with reduced damage for chaotic pack racing.',
  },
  {
    id: 'traffic-panic',
    name: 'TRAFFIC PANIC',
    detail: 'DENSE CITY FLOW',
    description: 'A denser deterministic traffic stream turns overtaking into a moving puzzle.',
  },
  {
    id: 'low-gravity',
    name: 'LOW GRAVITY',
    detail: 'LONGER AIRTIME',
    description: 'Reduced gravity makes ramps and mid-air control the centre of the event.',
  },
] as const;

const PRESET_IDS = new Set<ArcadePreset>(ARCADE_PRESETS.map((preset) => preset.id));

export function normaliseArcadePreset(value: unknown): ArcadePreset {
  return PRESET_IDS.has(value as ArcadePreset) ? value as ArcadePreset : 'standard';
}

export function arcadeCareerEligible(value: unknown): boolean {
  return normaliseArcadePreset(value) === 'standard';
}

export function arcadeGravityScale(value: unknown): number {
  return normaliseArcadePreset(value) === 'low-gravity' ? 0.52 : 1;
}

/** Arcade modifier combined with the track's own gravity (the Moon map runs
 * at a fraction of Earth's pull). Server and client prediction share it. */
export function raceGravityScale(map: { gravity?: number }, preset: unknown): number {
  const mapScale = typeof map.gravity === 'number' && Number.isFinite(map.gravity) ? map.gravity : 1;
  return Math.max(0.2, Math.min(2, arcadeGravityScale(preset) * mapScale));
}

export function arcadeNitroRegeneration(value: unknown): number {
  return normaliseArcadePreset(value) === 'nitro-rush' ? 25 : 0;
}

export function arcadeCollisionImpulseScale(value: unknown): number {
  return normaliseArcadePreset(value) === 'heavy-metal' ? 1.42 : 1;
}

export function arcadeDamageScale(value: unknown): number {
  return normaliseArcadePreset(value) === 'heavy-metal' ? 0.58 : 1;
}

export function arcadeTrafficDensity(value: unknown): 'normal' | 'panic' {
  return normaliseArcadePreset(value) === 'traffic-panic' ? 'panic' : 'normal';
}
